import { useCallback, useEffect, useState } from 'react';
import { calcularAlturaLivre, ALTURA_DE_RECURSO } from './alturaLinhaVideo';

// ALTURA DA LINHA DE VÍDEO
//
// A linha com as duas janelas de câmara passa a ocupar TODO o espaço que sobra
// no ecrã: do sítio onde começa até ao que estiver por baixo (chat, botões,
// barra fixa). Antes tinha uma altura fixa em vw/vh — num telemóvel dava
// ~155px de câmara e um buraco preto enorme por baixo.
//
// Mede-se em vez de adivinhar, porque o que está por baixo muda: o chat
// cresce com mensagens, os botões aparecem e desaparecem com a fase do jogo,
// e o Android esconde e mostra a barra do endereço.
//
// Uso:
//   const { linhaRef, barraRef, altura } = useAlturaLinhaVideo();
//   <div ref={linhaRef} style={{ height: altura, ... }}>   ← a linha de vídeo
//   <div ref={barraRef} style={{ position: 'fixed', bottom: 0 }}> ← barra fixa (opcional)
// Os irmãos em fluxo por baixo da linha são medidos sozinhos.
//
// Os refs são de CALLBACK e guardam o nó em estado, de propósito: os ecrãs de
// PK fazem `if (!batalha) return (...)` e a linha só é desenhada depois de a
// batalha chegar. Um useRef lido uma vez no mount via `null` e nunca mais
// media nada — foi exatamente isso que aconteceu na primeira versão.
//
// DIAGNÓSTICO: cada medição fica em window.gfl.medidaLinha e sai na consola.
// Com `debug` no endereço (ex.: /?arena&debug) aparece também um pequeno
// rótulo no canto do ecrã com os números — serve para ver no telemóvel sem
// ligar consola nenhuma.

// Altura de um elemento em fluxo, com as margens verticais.
function alturaComMargens(el) {
  const r = el.getBoundingClientRect();
  const cs = getComputedStyle(el);
  return r.height + (parseFloat(cs.marginTop) || 0) + (parseFloat(cs.marginBottom) || 0);
}

// Soma do que está por baixo da linha: os irmãos seguintes que estão em
// fluxo (absolutos e fixos não empurram nada) mais a barra fixa, se houver.
// Devolve também a lista do que contou, para o diagnóstico.
function medirAbaixo(linha, barra) {
  let total = 0;
  const contados = [];
  for (let el = linha.nextElementSibling; el; el = el.nextElementSibling) {
    const pos = getComputedStyle(el).position;
    if (pos === 'absolute' || pos === 'fixed') continue;
    const h = alturaComMargens(el);
    total += h;
    contados.push(`${el.tagName.toLowerCase()}${el.id ? '#' + el.id : ''}=${Math.round(h)}`);
  }
  if (barra && barra.isConnected) {
    const h = barra.getBoundingClientRect().height;
    total += h;
    contados.push(`barra=${Math.round(h)}`);
  }
  return { total, contados };
}

function emModoDebug() {
  try { return /[?&]debug/.test(window.location.search); } catch { return false; }
}

// Rótulo no canto do ecrã (só com ?debug).
function mostrarRotulo(texto) {
  let el = document.getElementById('gfl-debug-linha');
  if (!el) {
    el = document.createElement('div');
    el.id = 'gfl-debug-linha';
    el.style.cssText = 'position:fixed;top:2px;left:2px;z-index:2147483647;background:rgba(0,0,0,.85);color:#39FF14;'
      + 'font:11px/1.3 monospace;padding:4px 6px;border:1px solid #39FF14;border-radius:6px;pointer-events:none;max-width:95vw;white-space:pre-wrap';
    document.body.appendChild(el);
  }
  el.textContent = texto;
}

export function useAlturaLinhaVideo() {
  const [linha, setLinha] = useState(null);
  const [barra, setBarra] = useState(null);
  const [altura, setAltura] = useState(null);

  const linhaRef = useCallback((no) => setLinha(no), []);
  const barraRef = useCallback((no) => setBarra(no), []);

  useEffect(() => {
    if (typeof window === 'undefined' || !linha) return undefined;

    let ultima = null;
    const medir = () => {
      if (!linha.isConnected) return;
      const alturaEcra = window.innerHeight;
      const topo = linha.getBoundingClientRect().top;
      const abaixo = medirAbaixo(linha, barra);
      const nova = calcularAlturaLivre({ alturaEcra, topo, abaixo: abaixo.total });

      const medida = {
        ecra: Math.round(alturaEcra), topo: Math.round(topo), abaixo: Math.round(abaixo.total),
        contados: abaixo.contados.join(' '), altura: nova, largura: Math.round(linha.getBoundingClientRect().width),
      };
      window.gfl = { ...(window.gfl || {}), medidaLinha: medida };
      if (emModoDebug()) {
        mostrarRotulo(`ecrã ${medida.ecra} · topo ${medida.topo} · abaixo ${medida.abaixo} (${medida.contados})\n→ linha ${medida.altura}px (largura ${medida.largura})`);
      }

      if (nova !== ultima) {
        ultima = nova;
        setAltura(nova);
        console.log(`[linha de vídeo] ecrã ${medida.ecra} − topo ${medida.topo} − abaixo ${medida.abaixo} (${medida.contados}) → ${nova}px`);
      }
    };

    medir();

    // Tudo o que pode mudar o espaço disponível.
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(medir) : null;
    const observarVizinhos = () => {
      if (!ro) return;
      ro.disconnect();
      const pai = linha.parentElement;
      if (pai) Array.from(pai.children).forEach((f) => ro.observe(f));
      if (barra) ro.observe(barra);
    };
    observarVizinhos();
    const mo = typeof MutationObserver !== 'undefined' && linha.parentElement
      ? new MutationObserver(() => { observarVizinhos(); medir(); })
      : null;
    if (mo) mo.observe(linha.parentElement, { childList: true });

    window.addEventListener('resize', medir);
    window.visualViewport?.addEventListener('resize', medir);
    // Rede de segurança barata (só lê meia dúzia de retângulos).
    const relogio = setInterval(medir, 1500);

    return () => {
      if (ro) ro.disconnect();
      if (mo) mo.disconnect();
      window.removeEventListener('resize', medir);
      window.visualViewport?.removeEventListener('resize', medir);
      clearInterval(relogio);
    };
  }, [linha, barra]);

  return { linhaRef, barraRef, altura: altura ? `${altura}px` : ALTURA_DE_RECURSO };
}
